import _ from "lodash";
import React, { useEffect, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Icon, Button, Label, Segment, Header, Form, Grid, Divider } from "semantic-ui-react";
import { ControlledEmail } from "../../../components/ControlledEmail";
import { ControlledFileUploader } from "../../../components/ControlledFileUploader";
import {
  ControlledInput,
  ControlledTextArea,
  ControlledCheckbox,
  ControlledAccessibleDropdown,
  ControlledDropdownV2,
} from "../../../components/ControlledForm";
import { ControlledGeocoder } from "../../../components/ControlledGeocoder";
import { ControlledWholeNumber } from "../../../components/ControlledWholeNumber";

import identitySelectors from "../../../selectors/identity";

import "./FormSection.scss";

import { usePreventEnterOnInputElements } from "../../../hooks/usePreventEnterOnInputElements";
import { PermitsContact } from "../types/PermitsContact";
import { parseRubyHash } from "../../../utils/functions";
import usePermitContacts from "../hooks/usePermitContacts";
import { useParams } from "react-router-dom";
import { ContactInfoOptions } from "../types/ContactInfoOptions";

import { ControlledContactInfo } from "../../contacts/components/ControlledContactInfo";

import useDeepCompareEffect from "use-deep-compare-effect";
import camelcaseKeys from "camelcase-keys";
import { PermitsCustomFieldTypesNames } from "../../../constants/PermitsCustomFieldTypesNames";
import ControlledLocation from "../../../components/global_components/form_components/ControlledLocation";
import PermitFormCheckboxLabel from "./PermitFormCheckboxLabel";
import { ControlledPhoneNumber } from "../../../components/global_components/form_components/ControlledPhoneNumber";
import { AdditionalContactCards, ContactCard } from "./contacts/ContactCard";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import { ControlledDateTime } from "../../../components/global_components/form_components/accessible/DateTime";
import { DateValue } from "react-aria-components";
import { FormFieldLabel } from "./FormFieldLabel";

const getFullAddress = (address: string, address2: string, city: string, state: string, zip: string) => {
  const fullAddress = [address, address2, city, state, zip].filter((item) => item).join(", "); //creates full address string (filters out empty values)
  return {
    address: fullAddress,
    short_address: fullAddress,
    result: fullAddress,
  };
};

//get the field value from the payload
const getFieldValue = (personData: any, attributeLocation: string) => {
  const attributeData = personData ? _.get(personData, attributeLocation) : null;
  return attributeData;
};

export const FormField = (props: any) => {
  const {
    readonly,
    errors,
    fields, // can be replaced with index
    permitsCustomFields,
    permitsFormField,
    permitsExtensions,
    addOriginalField,
    stacked,
    type,
    onChangeInnerValue,
    renewalData,
    isCitizen,
    applicationForm,
    isApplyingScreen = false,
    isPublicView = false,
    person, //default payload
    error,
    isPrintView,
    fieldTypesToForceReadonly = [],
  } = props;

  const { setValue, control, getValues } = applicationForm;
  const index = _.findIndex(fields, (field: any) => permitsFormField.id === field.permits_form_field_id);
  if (index < 0) {
    return null;
  }
  const name = `permits_extensions_attributes.${index}.value`;
  const customField = _.get(permitsCustomFields, permitsFormField.attributes.permitsExtensionDefId);
  const customFieldFee = _.get(permitsFormField, "attributes.amount_difference", undefined);
  const isFeeExist = _.get(permitsFormField, "attributes.isFeeExist", undefined);
  const isControllingField = _.get(permitsFormField, "attributes.isControllingField", undefined);
  const permitsExtensionDefId = _.get(permitsFormField, "attributes.permitsExtensionDefId", undefined);
  // gets id of a permit extension (used to update a permit's form fields)
  const permitsExtensionId = useMemo(() => {
    const permitsExtensionsAttributes = getValues()?.permits_extensions_attributes;
    const permitsExtension = permitsExtensionsAttributes.find(
      (extension: any) => extension.permits_form_field_id === permitsFormField.id,
    );
    return permitsExtension?.id != null ? Number(permitsExtension?.id) : null;
  }, [getValues()]);
  const { id } = useParams<{ id: string }>();
  const { contacts } = usePermitContacts({ permitId: id });
  const customFieldName = _.get(customField, "attributes.name");
  const customFieldDescription = _.get(customField, "attributes.description");
  const minDaysRaw = permitsFormField.attributes.metadata?.minDaysNotice;
  const minDaysNotice = minDaysRaw && !isNaN(Number(minDaysRaw)) ? parseInt(minDaysRaw) : undefined;
  const isPublic = permitsFormField.attributes.isPublic;
  const customFieldType = _.get(customField, "attributes.type") as number;
  const isCheckbox = customFieldType === PermitsCustomFieldTypesNames?.CHECKBOX;
  const isDatePicker = customFieldType === PermitsCustomFieldTypesNames?.DATE_PICKER;
  const isContactInfoField = customFieldType === PermitsCustomFieldTypesNames.CONTACT_INFORMATION;
  const isAdditionalContactsField = customFieldType === PermitsCustomFieldTypesNames.ADDITIONAL_CONTACTS;

  const customFieldOptions = _.get(customField, "attributes.options");
  const customFieldPrimary = _.get(permitsFormField, "attributes.primary");
  const required = isCitizen
    ? _.get(permitsFormField, "attributes.applicantView", "optional") === "required"
    : _.get(permitsFormField, "attributes.staffView", "optional") === "required";
  const contactInfoOptions: ContactInfoOptions = parseRubyHash(
    _.get(permitsFormField, "attributes.contactInfoOptions"),
  ); // Map that describes whether contact info options are required, optional, hidden, readonly
  const isHidden = isCitizen
    ? _.get(permitsFormField, "attributes.applicantView", "optional") === "hidden"
    : _.get(permitsFormField, "attributes.staffView", "optional") === "hidden";
  const staffOnly = !isCitizen && _.get(permitsFormField, "attributes.applicantView", "optional") === "hidden";
  const isReadonly =
    (isCitizen
      ? _.get(permitsFormField, "attributes.applicantView", "optional") === "read_only"
      : _.get(permitsFormField, "attributes.staffView", "optional") === "read_only") || readonly;
  const customFieldError = _.get(errors, `permits_extensions_attributes.${index}.value.message`);
  //get default value for custom field
  const customFieldDefaultValue = _.get(customField, "attributes.defaultCustomValue"); //default value. code removed underlines

  //store all default values depending on type
  //get default value from person in switch statement
  const defaultCustomValue = useMemo(() => {
    if (!isApplyingScreen) return null;

    switch (customFieldDefaultValue) {
      case "applicant_first_name":
        return getFieldValue(person, "data.attributes.first-name");
      case "applicant_last_name":
        return getFieldValue(person, "data.attributes.last-name");
      case "applicant_email":
        return getFieldValue(person, "data.attributes.email");
      case "applicant_phone_number":
        return getFieldValue(person, "data.attributes.phone");
      case "applicant_mailing_address":
        const address = getFieldValue(person, "data.attributes.address");
        const address2 = getFieldValue(person, "data.attributes.address2");
        const city = getFieldValue(person, "data.attributes.city");
        const state = getFieldValue(person, "data.attributes.state");
        const zip = getFieldValue(person, "data.attributes.zip");
        return getFullAddress(address, address2, city, state, zip);
      case "applicant_home_address":
        const physicalAddress = getFieldValue(person, "data.attributes.home-address");
        const physicalAddress2 = getFieldValue(person, "data.attributes.home-address2");
        const physicalCity = getFieldValue(person, "data.attributes.home-city");
        const physicalState = getFieldValue(person, "data.attributes.home-state");
        const physicalZip = getFieldValue(person, "data.attributes.home-zip");
        return getFullAddress(physicalAddress, physicalAddress2, physicalCity, physicalState, physicalZip);
      case "applicant_license_number":
        return getFieldValue(person, "data.attributes.license-number");
      case "applicant_company":
        return getFieldValue(person, "data.attributes.company-name");
      default:
        return null;
    }
  }, [isApplyingScreen, customFieldDefaultValue, person]);

  // finds the contact associated with this custom field (contact info)
  function findContactFromContactsTable(): PermitsContact {
    return contacts.find((contact: PermitsContact) => contact.permitsExtensionDefId === permitsExtensionDefId);
  }

  // finds array of contacts associated with this custom field (additional contact info)
  function findAdditionalContactsFromContactsTable(): PermitsContact[] {
    return contacts.filter((contact: PermitsContact) => contact.permitsExtensionDefId === permitsExtensionDefId);
  }

  const shouldForceReadonly = fieldTypesToForceReadonly.includes(customFieldType);

  const getSingleSelectOptions = (): Array<{ key: string; value: string; text: string }> => {
    const getCurrentValue = (extension: any) => {
      return _.get(extension, ["data", "attributes", "value"], "");
    };
    const getCurrentExtensionDefId = (extension: any) => {
      return _.get(extension, ["data", "attributes", "permits-extension-def-id"]);
    };
    const hasSelectedOption = (options: { key: string; value: string; text: string }[], currentValue: string) => {
      return options.find((option: any) => option.key === currentValue) !== undefined;
    };

    // Get all of the options on the custom field currently (the most up to date options)
    let options = customFieldOptions?.map((option: string) => ({ key: option, value: option, text: option }));

    // If the permit does not have any extensions (permit does not exist yet) just return the options
    if (!options || permitsExtensions?.length === 0) {
      return options;
    }

    // Get the current value for the custom field on this permit from the permit extension
    const currentValueExtension = permitsExtensions?.find((extension: any) => {
      const extensionDefId = getCurrentExtensionDefId(extension);
      return extensionDefId === permitsExtensionDefId;
    });
    const currentValue = getCurrentValue(currentValueExtension);

    // If the current value is not empty AND is not an option, make it appear as a deactivated option
    if (currentValue != "" && !hasSelectedOption(options, currentValue)) {
      const currentOptionDeactivated = {
        key: currentValue,
        value: currentValue,
        text: currentValue,
        className: "tw-bg-[#fdf6f6] tw-text-[#81332f]",
      };
      options = [currentOptionDeactivated, ...options];
    }

    // Add in an Empty option to clear
    const emptyOption = {
      key: "",
      value: "",
      text: "",
    };

    return [emptyOption, ...options];
  };

  //render field dependent on custom field type
  const customFieldControl = (
    {
      //short input - no validation - default
      0: (
        <ControlledInput
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          transform={{
            input: (value: any) => value?.toString(),
            output: (value: any) => value,
          }}
          readonly={isReadonly || shouldForceReadonly}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //short input - email
      1: (
        <ControlledEmail
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          fieldName={customFieldName}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //short input - whole number
      2: (
        <ControlledWholeNumber
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          fieldName={customFieldName}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //short input - phone number
      3: (
        <ControlledPhoneNumber
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //single select
      4: isApplyingScreen ? (
        <ControlledAccessibleDropdown
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          options={getSingleSelectOptions()}
        />
      ) : (
        <ControlledDropdownV2
          id={name}
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          options={getSingleSelectOptions()}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //text area
      5: (
        <ControlledTextArea
          id={name}
          name={name}
          control={control}
          rows={3}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          onChange={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //google address geocoder
      6: (
        <ControlledGeocoder
          id={name}
          name={name}
          control={control}
          required={required}
          readonly={isReadonly || shouldForceReadonly}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          onBlur={!isReadonly && isFeeExist && onChangeInnerValue}
          setValue={setValue}
        />
      ),
      //date picker
      7: (
        <ControlledDateTime
          name={name}
          control={control}
          required={required}
          defaultValue={defaultCustomValue || getValues(name) || ""}
          readonly={isReadonly || shouldForceReadonly}
          onBlur={!isReadonly && isFeeExist && onChangeInnerValue}
          granularity='day'
          className='tw-border-border-dark'
          zonedDateTimeProps={{
            formatString: "PP",
            timeWithRespectToAgencyTimezone: false,
          }}
          isDateUnavailable={(date: DateValue): boolean => {
            // if there is no minDaysNotice (or it is 0), make all dates available
            // I have no idea what 'stacked' is, but according to the last datepicker, we don't block out any dates if this is true
            if (!minDaysNotice || stacked) return false;

            const tz = new window.Intl.DateTimeFormat().resolvedOptions().timeZone;
            const calendarDate = date.toDate(tz);

            const daysLater = new Date();
            daysLater.setDate(daysLater.getDate() + (minDaysNotice - 1));

            return calendarDate < daysLater;
          }}
          label={
            <FormFieldLabel
              name={name}
              customFieldName={customFieldName}
              staffOnly={staffOnly}
              isReadonly={isReadonly}
              isFeeExist={isFeeExist}
              isControllingField={isControllingField}
              stacked={stacked}
            />
          }
        />
      ),
      //file uploader
      8: (
        <ControlledFileUploader
          id={name}
          name={name}
          control={control}
          defaultValue={defaultCustomValue || Array.isArray(getValues(name)) ? getValues(name) : []} // Ensures defaultValue is Array- otherwise crashes if form type has been changed
          readonly={isReadonly || shouldForceReadonly}
          required={required}
          folder='permits'
          onBlur={!isReadonly && isFeeExist && onChangeInnerValue}
        />
      ),
      //checkbox
      9: (
        <ControlledCheckbox
          id={name}
          name={name}
          control={control}
          defaultValue={defaultCustomValue || getValues(name) || false}
          readonly={isReadonly || shouldForceReadonly}
          onBlur={!isReadonly && isFeeExist && onChangeInnerValue}
          customAriaLabel={!isReadonly && (isFeeExist || staffOnly) ? customFieldName : ""}
          label={
            !isReadonly && (isFeeExist || staffOnly) ? (
              <PermitFormCheckboxLabel
                customFieldName={customFieldName}
                staffOnly={staffOnly}
                stacked={stacked}
                isFeeExist={isFeeExist}
              />
            ) : (
              customFieldName
            )
          }
          required={!isReadonly && required}
        />
      ),
      //contact information
      10: isApplyingScreen ? (
        //component for applying screen
        <ControlledContactInfo
          formFieldId={permitsFormField.id}
          fieldType='contact_info'
          name={name}
          control={control}
          contactRole={customFieldName}
          readonly={isReadonly || shouldForceReadonly}
          required={required}
          contactInfoOptions={contactInfoOptions}
          defaultValues={getValues(name)}
          isApplyingScreen={isApplyingScreen}
        />
      ) : (
        //component for viewing application screen
        <ContactCard
          showEditButton={!isPublicView}
          contact={findContactFromContactsTable()}
          contactInfoOptions={contactInfoOptions}
          permitsExtensionId={permitsExtensionDefId}
          customFieldType={10}
          contactCanBeDeleted={!required}
          customFieldName={customFieldName}
          isPrintView={isPrintView}
          isPublicView={isPublicView}
        />
      ),
      //additional contacts
      11: isApplyingScreen ? (
        //component for applying screen
        <ControlledContactInfo
          formFieldId={permitsFormField.id}
          fieldType='additional_contacts'
          name={name}
          control={control}
          contactRole={customFieldName}
          readonly={isReadonly || shouldForceReadonly}
          required={required}
          contactInfoOptions={contactInfoOptions}
          defaultValues={getValues(name)}
          isApplyingScreen={isApplyingScreen}
        />
      ) : (
        //component for viewing application screen
        <AdditionalContactCards
          showEditButton={!isPublicView}
          contacts={findAdditionalContactsFromContactsTable()}
          contactInfoOptions={contactInfoOptions}
          permitsExtensionId={permitsExtensionId}
        />
      ),
      13: (
        <ControlledLocation
          id={name}
          control={control}
          name={name}
          defaultValue={getValues(name)}
          readonly={isReadonly || shouldForceReadonly} // Make sure it stays readonly in the staff view
          required={required}
          errors={errors}
          hideErrorMessage
          isPublicView={isPublicView}
          showEmptyFields={!isApplyingScreen}
        />
      ),
    } as any
  )[customFieldType];

  useEffect(() => {
    if (isFeeExist && addOriginalField && permitsExtensionId) {
      addOriginalField({
        ...permitsFormField.attributes,
        value: getValues(name),
      });
    }
  }, [isFeeExist, permitsExtensionId, applicationForm]);

  useEffect(() => {
    //EN-4822 - added check to see if data should be cleared on renewal
    if (
      name &&
      customField &&
      renewalData &&
      applicationForm &&
      permitsFormField &&
      !permitsFormField.attributes?.clearDataOnRenewal
    ) {
      const data = renewalData?.find((el: any) => el.attributes["permits-extension-def-id"] == customField.id);
      if (
        customField.attributes.type === PermitsCustomFieldTypesNames.PRIMARY_LOCATION ||
        customField.attributes.type === PermitsCustomFieldTypesNames.CONTACT_INFORMATION
      ) {
        //EN-6192 - When a contact information field is not required, wasn't set by the parent permit, and also not cleared on renewal, it should not be set in the form
        if (
          customField.attributes.type === PermitsCustomFieldTypesNames.CONTACT_INFORMATION &&
          !data?.attributes?.value?.["contact_type"]
        ) {
          return;
        }
        //EN-4822 - hacky fix to prevent the errors that come from the camelcaseKeys function
        delete data?.attributes?.value?.["contact_id"];
        delete data?.attributes?.value?.["contact-id"];
        if (!getValues(name)) setValue(name, camelcaseKeys(data?.attributes?.value ?? null, { deep: true }));
      } else {
        //EN-4822 - hacky fix to prevent render bug with address field with additional contacts
        if (customField.attributes.type !== PermitsCustomFieldTypesNames.ADDITIONAL_CONTACTS)
          setValue(name, data?.attributes?.value || "");
      }
    }
  }, [renewalData, customField, name, permitsFormField]);

  if ((!isPublic && type === "view") || isHidden) {
    return <div className='blank' />;
  }

  return (
    <Form.Field
      className={isCheckbox && stacked ? "align-checkbox-field" : ""}
      key={index}
      error={(!isReadonly && !!customFieldError) || (required && error)}
      required={!isReadonly && required}
      style={{ display: "flex" }}
    >
      <Controller
        as={<input type='hidden' />}
        name={`permits_extensions_attributes.${index}.permits_form_field_id`}
        defaultValue={permitsFormField.id}
      />
      <Controller
        as={<input type='hidden' />}
        name={`permits_extensions_attributes.${index}.permits_extension_def_id`}
        defaultValue={permitsFormField.attributes.permitsExtensionDefId}
      />

      {/* If this is a contact info field, add a contact-id field to the value */}
      {isApplyingScreen && isContactInfoField && (
        <Controller
          as={<input type='hidden' />}
          name={`permits_extensions_attributes.${index}.value.contact-id`}
          defaultValue={undefined}
        />
      )}

      {customFieldPrimary && (
        <Controller
          as={<input type='hidden' className={"primary_hidden_field"} />}
          name={`permits_extensions_attributes.${index}.primary`}
          defaultValue={customFieldPrimary}
        />
      )}
      {!isCheckbox && !isDatePicker && (
        <FormFieldLabel
          name={name}
          customFieldName={customFieldName}
          staffOnly={staffOnly}
          isReadonly={isReadonly}
          isFeeExist={isFeeExist}
          isControllingField={isControllingField}
          stacked={stacked}
          renderAsLabel={!isContactInfoField && !isAdditionalContactsField}
        />
      )}
      {stacked && customFieldDescription && !isCheckbox && <p style={{ marginBottom: 4 }}>{customFieldDescription}</p>}
      {customFieldControl}
      {stacked && customFieldDescription && isCheckbox && (
        <p className='checkbox-field-description'>{customFieldDescription}</p>
      )}
      {!isReadonly && customFieldError && (
        <Label basic prompt pointing='above' aria-live='polite' id={`${name}-error`}>
          {customFieldError}
        </Label>
      )}
      {!isReadonly && isFeeExist && isControllingField && !!customFieldFee && (
        <label htmlFor={name} className='control-description'>
          This change will <u>{customFieldFee >= 0 ? "increase" : "decrease"}</u> the record’s fees by $
          {Math.abs(customFieldFee / 100)}
        </label>
      )}
    </Form.Field>
  );
};
function LinkRenderer(props: any) {
  return (
    <a
      href={props.href}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={`${props.children} (opens in a new tab)`}
    >
      {props.children}
    </a>
  );
}

export const PermitsFormSection = (props: any) => {
  const {
    onBack,
    onNext,
    hasMore,
    lastSection,
    section,
    permitsCustomFields,
    permitsExtensions,
    readonly,
    stacked,
    enforceRequired,
    type,
    addOriginalField,
    onChangeInnerValue,
    renewalData,
    person, //contact data
    isApplyingScreen = false,
    isPublicView = false,
    isPrintView,
    setPrintableFormFields,
    fieldTypesToForceReadonly = [],
  } = props;
  const applicationForm = useFormContext();
  const { setValue, handleSubmit, errors, watch } = applicationForm;
  const isCitizen = identitySelectors.isCitizen();
  const fields: any[] = watch("permits_extensions_attributes");

  useDeepCompareEffect(() => {
    if (!isPrintView) return;
    setPrintableFormFields(fields);
  }, [fields]);

  usePreventEnterOnInputElements();

  const onSubmit = (data: any) => {
    data = data.permits_extensions_attributes;
    _.each(data, (value: any) => {
      _.each(value, (state: any, index: number) => {
        if (state) {
          setValue(`permits_extensions_attributes.${index}.value`, state.value);
        }
      });
    });
    stacked && onNext();
  };
  const fieldsList = (section.fields || []).sort((a: any, b: any) => a.attributes.order - b.attributes.order);

  return (
    <>
      {!stacked && (
        <Form>
          <Grid>
            <Grid.Column width={16}>
              {section.attributes.name && (
                <Header as='h3' style={{ fontWeight: 300 }}>
                  <Header.Content>{section.attributes.name}</Header.Content>
                </Header>
              )}
              <div className='section-container'>
                {_.compact(
                  fieldsList.map((permitsFormField: any, index: number) => {
                    const isHidden = isCitizen
                      ? permitsFormField.attributes.applicantView === "hidden"
                      : permitsFormField.attributes.staffView === "hidden";

                    const isFullWidthField = [PermitsCustomFieldTypesNames.PRIMARY_LOCATION].some((fieldType) => {
                      return (
                        permitsCustomFields[permitsFormField.attributes.permitsExtensionDefId]?.attributes.type ===
                        fieldType
                      );
                    });

                    return (
                      <div
                        key={index}
                        className={classNames("tw-min-w-[calc(50%-25px)]", {
                          "tw-block": isHidden,
                          "tw-w-full": isFullWidthField,
                        })}
                      >
                        <FormField
                          applicationForm={applicationForm}
                          readonly={readonly}
                          enforceRequired={enforceRequired}
                          errors={errors}
                          fields={fields}
                          permitsCustomFields={permitsCustomFields}
                          permitsFormField={permitsFormField}
                          addOriginalField={addOriginalField}
                          permitsExtensions={permitsExtensions}
                          type={type}
                          onChangeInnerValue={onChangeInnerValue}
                          isCitizen={isCitizen}
                          person={person}
                          isApplyingScreen={isApplyingScreen}
                          isPublicView={isPublicView}
                          isPrintView={isPrintView}
                          fieldTypesToForceReadonly={fieldTypesToForceReadonly}
                        />
                      </div>
                    );
                  }),
                )}
              </div>
            </Grid.Column>
          </Grid>
          {!lastSection && <Divider />}
        </Form>
      )}
      {stacked && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          {section.attributes.name && (
            <div style={{ marginBottom: 24 }}>
              <Header as='h2'> {section.attributes.name} </Header>
              <ReactMarkdown components={{ a: LinkRenderer }}>{section.attributes.description}</ReactMarkdown>
            </div>
          )}
          {_.compact(
            (section.fields || []).map((permitsFormField: any, index: number) => (
              <FormField
                applicationForm={applicationForm}
                stacked={stacked}
                key={index}
                renewalData={renewalData}
                readonly={readonly}
                enforceRequired={enforceRequired}
                permitsExtensions={permitsExtensions}
                errors={errors}
                fields={fields}
                permitsCustomFields={permitsCustomFields}
                permitsFormField={permitsFormField}
                isCitizen={isCitizen}
                person={person}
                isApplyingScreen={isApplyingScreen}
                isPublicView={isPublicView}
                isPrintView={isPrintView}
                fieldTypesToForceReadonly={fieldTypesToForceReadonly}
              />
            )),
          )}
          <Segment basic clearing style={{ marginTop: "auto", padding: "32px 0px 4px 0px" }}>
            {onBack && (
              <Button className='tertiary' floated='left' size='large' onClick={onBack}>
                <Icon name='long arrow alternate left' />
                Back
              </Button>
            )}
            {onNext && <Button size='large' primary content={hasMore ? "Next" : "Submit"} floated='right' />}
          </Segment>
        </Form>
      )}
    </>
  );
};
